import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * ListRedirectPage - 处理 /list 路径下的哈希路由重定向
 * 支持将 /list#/index/carList 重定向到 /ai-fleet/gpt-plus
 * 支持将 /list#/index/carList/claude 重定向到 /ai-fleet/claude
 * 等等...
 * 
 * 调试信息：
 * - 检查浏览器控制台日志
 * - 确保 URL 格式正确
 * - 验证路由配置
 */
const ListRedirectPage = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const location = useLocation();

 useEffect(() => {
 // 添加更强的调试日志
 const handleRedirect = () => {
 try {
 // 获取完整URL信息
 const fullUrl = window.location.href;
 const hash = window.location.hash;
 const pathname = window.location.pathname;
 const search = window.location.search;
 
 console.log('=== ListRedirectPage Debug Info ===');
 console.log('Full URL:', fullUrl);
 console.log('Pathname:', pathname);
 console.log('Hash:', hash);
 console.log('Search:', search);
 console.log('React Location:', location);
 console.log('====================================');
 
 // 检查是否包含 /list 路径
 if (fullUrl.includes('/list') || pathname === '/list' || pathname.startsWith('/list/')) {
 console.log('✅ Detected /list route');
 
 // 检查哈希值
 if (hash) {
 console.log('✅ Hash found:', hash);
 // 移除开头的 #
 const hashPath = hash.substring(1);
 console.log('Cleaned hash path:', hashPath);
 
 // 处理 /index/carList 及其子路径
 if (hashPath.includes('/index/carList') || hashPath.includes('index/carList')) {
 console.log('✅ Found carList in hash');
 
 // 提取子路径 - 更容错的匹配
 let subPath = '';
 if (hashPath.includes('/index/carList')) {
 subPath = hashPath.split('/index/carList')[1] || '';
 } else if (hashPath.includes('index/carList')) {
 subPath = hashPath.split('index/carList')[1] || '';
 }
 
 console.log('Sub path after carList:', subPath);
 
 if (subPath === '' || subPath === '/') {
 // /list#/index/carList -> /ai-fleet/gpt-plus (默认到GPT Plus车队)
 console.log('✅ Redirecting to default: /ai-fleet/gpt-plus');
 window.location.replace('/ai-fleet/gpt-plus');
 return;
 } else {
 // 移除开头的 /
 const fleetType = subPath.startsWith('/') ? subPath.substring(1) : subPath;
 console.log('Extracted fleet type:', fleetType);
 
 // 映射车队类型
 const fleetMapping = {
 'gpt-backup': 'gpt-plus',
 'gpt-plus': 'gpt-plus',
 'claude': 'claude',
 'claude-pro': 'claude',
 'sora': 'sora',
 'grok': 'grok',
 'grok-super': 'grok'
 };
 
 const mappedFleetType = fleetMapping[fleetType] || 'gpt-plus';
 const targetPath = `/ai-fleet/${mappedFleetType}`;
 
 console.log(`✅ Redirecting to: ${targetPath}`);
 window.location.replace(targetPath);
 return;
 }
 }
 } else {
 console.log('⚠️ No hash found, using default redirect');
 }
 
 // 默认情况：无哈希或其他哈希路径，重定向到AI Fleet默认页面
 console.log('✅ Using default redirect to: /ai-fleet/gpt-plus');
 window.location.replace('/ai-fleet/gpt-plus');
 } else {
 console.log('❌ Not a /list route, pathname:', pathname);
 // 如果不是 /list 路径，但是组件被加载了，说明有问题
 console.log('⚠️ Unexpected route, redirecting to AI Fleet anyway');
 window.location.replace('/ai-fleet/gpt-plus');
 }
 } catch (error) {
 console.error('❌ Error in handleRedirect:', error);
 // 如果出错，就跳转到默认页面
 window.location.replace('/ai-fleet/gpt-plus');
 }
 };
 
 // 延迟执行以确保组件完全加载
 const timeoutId = setTimeout(handleRedirect, 100);
 
 // 也监听hashchange事件
 const handleHashChange = () => {
 console.log('Hash changed event triggered');
 setTimeout(handleRedirect, 50);
 };
 
 window.addEventListener('hashchange', handleHashChange);
 
 return () => {
 clearTimeout(timeoutId);
 window.removeEventListener('hashchange', handleHashChange);
 };
 }, [navigate, location]);

 // 显示重定向加载页面
 return (
 <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
 <div className="text-center">
 <div className="relative mb-8">
 <div className="w-20 h-20 border-4 border-ink-200 rounded-full"></div>
 <div className="w-20 h-20 border-4 border-accent-500 rounded-full border-t-transparent animate-spin absolute inset-0"></div>
 </div>
 <div className="space-y-2">
 <p className="text-lg font-medium text-ink-700">{t('common.loading', '加载中...')}</p>
 <p className="text-sm text-ink-500">{t('aiFleet.loading', '正在加载车队数据...')}</p>
 </div>
 </div>
 </div>
 );
};

export default ListRedirectPage;
